import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import axios from "axios";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import PlaceIcon from "@mui/icons-material/Place";
import PersonIcon from "@mui/icons-material/Person";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import { useLocation, useNavigate } from "react-router-dom";
import { BASE_URL } from "../Constants";
import { LanguageContext } from "../context/LanguageContext";
import { useAuth } from "../hooks/AuthProvider";
import { countries } from "../lib/countries";
import contactTranslations from "../languages/contact.json";
import "./ContactUs.css";
import "./edit.css";

const CONTACT_EMAIL = "info@sama-tech.ae";
const CONTACT_PHONE = "+971 58 114 9388";
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const defaultCountry =
  countries.find((country) => country.name === "United Arab Emirates") ||
  countries.find((country) => country.name === "United States") ||
  { name: "United States", code: "+1", position: "" };

const getCurrentCountry = (countryName) => {
  return countries.find((country) => country.name === countryName) || defaultCountry;
};

const getCountryFromPhoneInput = (phoneValue, preferredCountryName) => {
  const normalizedPhone = String(phoneValue || "").replace(/[^\d+]/g, "");

  if (!normalizedPhone.startsWith("+")) {
    return null;
  }

  const matches = countries.filter((country) => normalizedPhone.startsWith(country.code));
  if (matches.length === 0) {
    return null;
  }

  const sortedMatches = [...matches].sort((a, b) => b.code.length - a.code.length);
  const preferredMatch = sortedMatches.find((country) => country.name === preferredCountryName);
  if (preferredMatch) {
    return preferredMatch;
  }

  return sortedMatches.find((country) => country.name === defaultCountry.name) || sortedMatches[0];
};

const getCountryFlagStyle = (country) => {
  if (country?.flagImage) {
    return {
      backgroundImage: `url(${country.flagImage})`,
      backgroundPosition: "center",
      backgroundSize: "cover",
    };
  }

  return {
    backgroundPosition: country?.position || "0px 0px",
  };
};

export const ContactUs = ({ embedded = false }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { lang } = useContext(LanguageContext);
  const { authed, user } = useAuth();
  const t = contactTranslations[lang]?.contactUs || contactTranslations.en.contactUs;
  const isRtl = ["ar", "ur"].includes(lang);
  const userAutofillAppliedRef = useRef(false);
  const countryMenuRef = useRef(null);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: defaultCountry.code,
    country: defaultCountry.name,
    message: "",
  });
  const [isCountryOpen, setIsCountryOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState({ type: "", message: "" });

  useEffect(() => {
    const state = location.state || {};
    const stateCountry = getCountryFromPhoneInput(state.phone, state.country) || getCurrentCountry(state.country);
    setForm((prev) => ({
      ...prev,
      name: state.name || prev.name,
      email: state.email || prev.email,
      phone: state.phone || prev.phone,
      country: stateCountry?.name || prev.country,
      message: state.message || prev.message,
    }));
  }, [location.state]);

  useEffect(() => {
    if (!authed || !user?.id || userAutofillAppliedRef.current) return;

    const userCountry = getCountryFromPhoneInput(user.phone, user.country) || getCurrentCountry(user.country);
    setForm((prev) => ({
      ...prev,
      name: prev.name || user.name || user.fullName || "",
      email: prev.email || user.email || "",
      phone: prev.phone && prev.phone !== defaultCountry.code ? prev.phone : user.phone || defaultCountry.code,
      country: userCountry?.name || prev.country || defaultCountry.name,
    }));
    userAutofillAppliedRef.current = true;
  }, [authed, user]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!isCountryOpen) return;
      if (countryMenuRef.current && !countryMenuRef.current.contains(event.target)) {
        setIsCountryOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isCountryOpen]);

  const updateField = (key, value) => {
    setStatus({ type: "", message: "" });
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handlePhoneChange = useCallback((phoneValue) => {
    setStatus({ type: "", message: "" });
    setForm((prev) => {
      const matchedCountry = getCountryFromPhoneInput(phoneValue, prev.country);
      return {
        ...prev,
        phone: phoneValue,
        country: matchedCountry ? matchedCountry.name : prev.country,
      };
    });
  }, []);

  const selectCountryItem = useCallback((event, country) => {
    event.stopPropagation();
    if (!country?.name || !country?.code) return;

    setStatus({ type: "", message: "" });
    setForm((prev) => ({
      ...prev,
      country: country.name,
      phone: country.code,
    }));
    setIsCountryOpen(false);
  }, []);

  const copyText = async (value) => {
    try {
      await navigator.clipboard.writeText(value);
      setStatus({ type: "success", message: t.copied });
    } catch (_err) {
      setStatus({ type: "error", message: t.copyFailed });
    }
  };

  const submitMessage = async (event) => {
    event.preventDefault();
    const payload = {
      name: form.name.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      country: form.country,
      message: form.message.trim(),
    };

    if (!payload.name || !payload.email || !payload.message) {
      setStatus({ type: "error", message: t.requiredFields });
      return;
    }

    if (!EMAIL_REGEX.test(payload.email)) {
      setStatus({ type: "error", message: t.invalidEmail || "Please enter a valid email address." });
      return;
    }

    try {
      setSubmitting(true);
      await axios.post(`${BASE_URL}/auth/contact`, payload, {
        headers: { "Content-Type": "application/json" },
      });
      setStatus({
        type: "success",
        message: t.successMessage,
      });
      setForm((prev) => ({ ...prev, message: "" }));
    } catch (error) {
      setStatus({
        type: "error",
        message: error?.response?.data?.message || t.sendFailed,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const currentCountry = getCountryFromPhoneInput(form.phone, form.country) || getCurrentCountry(form.country);

  return (
        <main
          className={`contact-us-page ${embedded ? "contact-us-page-embedded" : "contact-us-page-public"}`}
          dir={isRtl ? "rtl" : "ltr"}
        >
          {!embedded ? (
            <div className="contact-us-public-head">
              <div className="contact-us-logo">
                <span className="contact-us-logo-bars" aria-hidden="true">
                  <i /><i /><i /><i /><i />
                </span>
                <strong>Sama-IOT</strong>
              </div>
              <button type="button" className="contact-us-back" onClick={() => navigate(-1)}>
                <ChevronLeftIcon />
                {t.back}
              </button>
            </div>
          ) : null}

          {embedded ? (
          <section className="contact-us-hero">
            <h1>{t.heroTitle}</h1>
            <h2>{t.heroSubtitle}</h2>
            <p>{t.heroDescription}</p>
          </section>
          ) : null}

          <section className="contact-us-layout">
            <form className="contact-us-form-panel" onSubmit={submitMessage}>
              <div className="contact-us-panel-head">
                <h3>{embedded ? t.supportTitle : t.pageTitle}</h3>
                <p>
                  {embedded
                    ? t.embeddedDescription
                    : t.publicDescription}
                </p>
              </div>

          <label className="contact-us-field">
            <span>{t.fullName}</span>
            <div className="contact-us-input-wrap">
              <PersonIcon />
              <input
                type="text"
                value={form.name}
                placeholder={t.fullNamePlaceholder}
                onChange={(event) => updateField("name", event.target.value)}
              />
            </div>
          </label>

          <label className="contact-us-field">
            <span>{t.emailAddress}</span>
            <div className="contact-us-input-wrap">
              <EmailIcon />
              <input
                type="email"
                value={form.email}
                placeholder={t.emailPlaceholder}
                onChange={(event) => updateField("email", event.target.value)}
              />
            </div>
          </label>

          <label className="contact-us-field">
            <span>{t.phoneNumber}</span>
            <div className="contact-us-input-wrap contact-us-phone" ref={countryMenuRef}>
              <input
                type="tel"
                value={form.phone}
                placeholder={t.phonePlaceholder}
                onChange={(event) => handlePhoneChange(event.target.value)}
              />
              <div
                className="edit-select-country contact-us-country-select"
                title={t.selectCountry}
                onClick={() => setIsCountryOpen((prev) => !prev)}
              >
                <div className="edit-country-flag" style={getCountryFlagStyle(currentCountry)} />
                <span className={`edit-arrow ${isCountryOpen ? "selected" : ""}`} />
                <ul
                  className={`edit-country-list ${isCountryOpen ? "selected" : ""}`}
                  onClick={(event) => event.stopPropagation()}
                >
                  {countries.map((country, idx) => (
                    <li
                      className="edit-country-list-item"
                      key={`${country.name}-${country.code}-${idx}`}
                      onClick={(event) => selectCountryItem(event, country)}
                    >
                      <span className="edit-country-flag" style={getCountryFlagStyle(country)} />
                      <span className="edit-country-name">{country.name}</span>
                      <span className="edit-country-code">{country.code}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </label>

          <label className="contact-us-field">
            <span>{t.description}</span>
            <textarea
              value={form.message}
              placeholder={t.descriptionPlaceholder}
              onChange={(event) => updateField("message", event.target.value)}
            />
          </label>

          {status.message ? (
            <div className={`contact-us-status contact-us-status-${status.type}`}>
              {status.message}
            </div>
          ) : null}

          <button className="contact-us-submit" type="submit" disabled={submitting}>
            {submitting ? t.sending : t.sendMessage}
          </button>
            </form>

            <aside className="contact-us-info-card">
              <h3>{t.contactInformation}</h3>

          <div className="contact-us-info-row">
            <span className="contact-us-info-icon"><EmailIcon /></span>
            <div>
              <span>{t.email}</span>
              <strong>{CONTACT_EMAIL}</strong>
            </div>
            <button type="button" onClick={() => copyText(CONTACT_EMAIL)} aria-label={t.copyEmail}>
              <ContentCopyIcon />
            </button>
          </div>

          <div className="contact-us-info-row">
            <span className="contact-us-info-icon"><PhoneIcon /></span>
            <div>
              <span>{t.phoneNumber}</span>
              <strong>{CONTACT_PHONE}</strong>
            </div>
            <button type="button" onClick={() => copyText(CONTACT_PHONE)} aria-label={t.copyPhone}>
              <ContentCopyIcon />
            </button>
          </div>

          <div className="contact-us-info-row">
            <span className="contact-us-info-icon"><PlaceIcon /></span>
            <div>
              <span>{t.location}</span>
              <strong>{t.contactLocation}</strong>
            </div>
          </div>
            </aside>
          </section>
        </main>
  );
};
